// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Trend signal cylinder context helpers + the canonical trendSignalDefinitions
// catalog, extracted from app.js. The definitions reference per-signal
// "specific" helpers (in trend-signal-specifics.js) and the local
// trendCylinderLabel / trendCylinderListLabel functions, so the whole bundle
// is exposed as a factory:
//
//   const { trendSignalDefinitions, trendCylinderLabel, ... } =
//     createTrendSignalContext({ ...deps });
//
// app.js destructures the returned helpers and the array, then uses them
// under the original names.
//
// The owner-facing copy for each signal (label/title/action/meaning/why/contexts/
// checks) is declared once in the TREND_METRIC_COPY registry below and merged with the
// per-signal render closures (affected / flagged / specific*) by the factory, so a
// signal's prose lives in one declarative place instead of being interleaved through the
// catalog. The registry stays in this module (rather than trend-availability.js) so the
// concatenated VM test harnesses — which already include this file but not the
// availability helpers — resolve it without a cross-module import.

export const TREND_METRIC_COPY = {
  sensorScore: {
    label: "Data Quality",
    title: "Data quality needs review",
    action: "Review suspect probes, wiring, missing channels, or monitor changes before treating related engine cues as mechanical.",
    meaning: "This trend is about trust in the logged data. A sensor-quality cue means one or more channels may be dropping out, jumping, reading out of range, or disagreeing with related probes.",
    why: "Bad probe data can make a healthy engine look suspicious, or hide a real pattern behind noise. Treat this as the first layer to resolve before leaning on dependent engine findings.",
    contexts: [
      "Probe or wiring issue",
      "Recent monitor or probe work",
      "Missing channels in older logs",
      "Parser/source differences"
    ],
    checks: [
      "Open a representative flagged flight and inspect the suspect channel against peer cylinders.",
      "Compare the same-cylinder CHT/EGT pair for corroboration.",
      "Check whether the cue started after probe, harness, monitor, or maintenance work.",
      "Ask your mechanic to review the probe/wiring before treating related engine cues as mechanical."
    ]
  },
  turboScore: {
    label: "Turbo/Boost",
    title: "Turbo or boost behavior needs review",
    action: "Compare MAP, TIT, fuel flow, RPM, altitude, and profile limits, then discuss repeat cues with a turbo-aware mechanic.",
    meaning: "This trend looks for repeated turbo-related review cues such as MAP, TIT, upper-deck pressure, or temperature behavior that deserves context.",
    why: "Turbocharged engines depend on boost-control hardware and temperature margins. Repeated cues should be checked against the aircraft's POH/AFM limits and the installed engine setup.",
    contexts: [
      "Missing POH/AFM limits in the profile",
      "High-power climb or altitude operations",
      "Pilot technique and mixture",
      "Wastegate/controller/intercooler behavior"
    ],
    checks: [
      "Confirm the aircraft profile has MAP and TIT limits.",
      "Compare flagged flights by altitude, power, RPM, fuel flow, and OAT.",
      "Check whether the trend started after turbo, exhaust, induction, or engine-control maintenance.",
      "Discuss repeated high-temperature or boost-control cues with a mechanic familiar with the installation."
    ]
  },
  gamiSweepScore: {
    label: "GAMI Sweep",
    title: "Saved GAMI sweep needs trend review",
    action: "Compare saved GAMI sweeps only against other intentional sweeps with similar power, procedure, and confidence.",
    meaning: "This trend uses pilot-marked GAMI sweep results. It is not an always-on engine detector; it tracks saved injector-balance test results across flights.",
    why: "A repeated GAMI spread above the usual target can be useful injector-balance context, but each result depends on whether the sweep started rich, reached the peaks, and was performed consistently.",
    contexts: [
      "Sweep technique",
      "TIT-managed turbo sweep speed",
      "Power setting",
      "Fuel-flow signal quality",
      "Injector balance history"
    ],
    checks: [
      "Open the saved sweep window and confirm the marked start/end points.",
      "Compare spread, confidence, and peak count against prior saved sweeps.",
      "Treat low-confidence or very short sweeps as context, not a completed injector-balance test.",
      "Use repeated high-confidence sweeps before drawing conclusions about injector balance."
    ]
  },
  oilSystemScore: {
    label: "Oil System",
    title: "Oil-system behavior needs review",
    action: "Compare oil pressure and oil temperature against RPM, phase, and the aircraft's published operating limits.",
    meaning: "This area watches running oil pressure and oil temperature for repeated excursions outside the normal review bands.",
    why: "Oil pressure and oil temperature are time-sensitive engine context. Repeated oil-system items should be checked against aircraft limits, oil quantity and viscosity, sensor scaling, engine phase, and recent maintenance.",
    contexts: [
      "Low oil quantity",
      "Oil viscosity or cold-oil behavior",
      "Restricted oil cooling airflow",
      "Pressure relief valve or pump behavior",
      "Oil temperature or pressure sender scaling"
    ],
    checks: [
      "Open the flagged oil windows with RPM and phase visible.",
      "Confirm the monitor channel scaling and the aircraft's published oil-pressure and oil-temperature limits.",
      "Compare nearby flights for the same oil-system pattern.",
      "If low oil pressure repeats during running samples, discuss it promptly with a mechanic."
    ]
  },
  chtScore: {
    label: "CHT Heat",
    title: "CHT heat exposure needs review",
    action: "Compare climb speed, power, mixture, OAT, baffling, and cowl-seal history.",
    meaning: "This trend means cylinder-head temperature exposure is repeating across flights. It may be one cylinder, a phase such as climb, or a broader heat-management pattern.",
    why: "CHT trends are useful for spotting cooling-airflow or operating changes before they become expensive. A hot cylinder is not automatically a bad cylinder; context matters.",
    contexts: [
      "Slow climb speed",
      "High power or lean mixture",
      "Hot OAT",
      "Baffling or cowl-seal leakage",
      "Probe placement or sensor change"
    ],
    checks: [
      "Compare CHT by cylinder during the same phase, especially climb.",
      "Review climb airspeed, power, mixture, OAT, and fuel flow.",
      "Check whether the same cylinder repeats or whether all cylinders move together.",
      "Ask your mechanic about baffling, cowl seals, probe placement, or recent cylinder work if the same cylinder persists."
    ]
  },
  egtScore: {
    label: "EGT Balance",
    title: "EGT balance cue is repeating",
    action: "Compare stable power settings, mixture technique, and recent induction or injector work.",
    meaning: "EGT balance means the cylinders are reasonably close during stable, extended operating periods. This area looks for repeated EGT spread or cylinder-to-cylinder balance changes in those steady windows.",
    why: "Raw EGT differences do not diagnose a problem by themselves. Repeated balance changes are most useful when compared in stable power and mixture conditions.",
    contexts: [
      "Different leaning technique",
      "Different power setting",
      "Probe placement differences",
      "Fuel injector or induction differences"
    ],
    checks: [
      "Compare stable cruise or runup windows, not mixed phases.",
      "Check whether the same cylinder is consistently high or low relative to peers.",
      "Look for matching CHT movement; EGT alone is weaker evidence.",
      "If available, perform or review a proper mixture sweep before drawing injector conclusions."
    ]
  },
  valveScore: {
    label: "EGT Rhythm",
    title: "EGT rhythm cue is repeating",
    action: "Treat repeated same-cylinder rhythm as an advisory cue to discuss with a mechanic or analyst.",
    meaning: "This trend looks for repeating rhythmic EGT movement on a cylinder during stable operation because that pattern can be an early review cue for a possible exhaust-valve anomaly.",
    why: "A repeated same-cylinder EGT rhythm can be an early review cue for exhaust-valve behavior, including a valve beginning to fail, but it can also come from sensor or operating effects. Confirmation requires inspection, not the graph alone.",
    contexts: [
      "Possible exhaust-valve anomaly",
      "EGT probe or wiring issue",
      "Unstable power/mixture window",
      "Short or noisy sample window"
    ],
    checks: [
      "Check whether the same cylinder repeats across multiple stable flights.",
      "Compare the EGT trace with same-cylinder CHT and peer EGTs.",
      "Avoid interpreting it during unstable power, mixture, or descent transitions.",
      "If repeated on the same cylinder, share the trend with a mechanic or analyst and consider borescope context."
    ]
  },
  combustionScore: {
    label: "CHT/EGT Pairing",
    title: "CHT/EGT pairing cue is repeating",
    action: "Compare mixture, fuel distribution, induction, and sensor context before treating the pattern as mechanical.",
    meaning: "This trend compares how a cylinder's CHT and EGT move together. The app is looking for repeated pairings that stand out from the other cylinders.",
    why: "CHT/EGT pairings can provide better context than either trace alone, but the meaning depends heavily on mixture side, power, airflow, and sensor quality.",
    contexts: [
      "Mixture distribution",
      "Induction leak or injector imbalance",
      "Cooling airflow difference",
      "Probe or wiring issue",
      "Different operating phase"
    ],
    checks: [
      "Compare the affected cylinder against peer cylinders in the same phase.",
      "Look for whether CHT and EGT rise or fall together or move opposite directions.",
      "Check fuel flow, RPM, MAP, and mixture context.",
      "Use this as a discussion point for injector, induction, cooling, or probe review."
    ]
  },
  inductionLeakScore: {
    label: "Induction Leak",
    title: "Induction-leak cue needs review",
    action: "Inspect the marked power-reduction windows with fuel flow, EGT, MAP when available, and RPM when available.",
    meaning: "This trend tracks cylinder-specific EGT responses during power reductions, including a smaller EGT drop than peers between settled MAP periods.",
    why: "These are passive review cues. Unknown mixture settings, ignition, fuel distribution, and probe behavior can produce similar responses; repeated comparable evidence is needed to interpret the pattern.",
    contexts: [
      "Throttle reduction",
      "Intake leak or loose induction coupling",
      "Mixture or fuel-metering change",
      "MAP or RPM channel missing",
      "Probe response during rapid transitions"
    ],
    checks: [
      "Open representative flagged flights and inspect the highlighted 3-7 second windows.",
      "Confirm fuel flow dropped while EGT rose at least 100 F.",
      "When available, check whether MAP also dropped and RPM moved as expected.",
      "If repeatable, discuss intake boots, gaskets, clamps, and induction plumbing with a mechanic."
    ]
  },
  magScore: {
    label: "Mag Check",
    title: "Mag-check cue needs review",
    action: "Compare runup quality, RPM stability, fuel flow, and EGT response; use this as a discussion point, not left/right attribution.",
    meaning: "This trend summarizes repeated cues found in runup or mag-check-like segments.",
    why: "A good mag check is not just RPM drop; EGT response matters too. Repeated weak response on one cylinder can point toward plug, lead, ignition, mixture, or test-quality context.",
    contexts: [
      "No clean mag-check segment logged",
      "Fuel flow or throttle changed during runup",
      "Mixture not leaned for the check",
      "Plug/lead/mag behavior"
    ],
    checks: [
      "Open the flagged runup window and confirm the mag-check segment looks real.",
      "Compare RPM stability, fuel flow, and EGT rise across cylinders.",
      "Look for the same weak cylinder repeating.",
      "Use the result as a runup/ignition discussion point; do not assume left/right mag attribution without annotations."
    ]
  },
  carbIceScore: {
    label: "Carb Ice",
    title: "Carb-ice context is repeating",
    action: "Compare carb temperature, OAT, RPM/MAP movement, weather, and carb-heat use before treating this as a cause.",
    meaning: "This trend looks for repeated carb-temperature, OAT, and power-movement context that may deserve carb-ice review.",
    why: "Carb ice depends on weather, carb temperature, power setting, and pilot actions. The data can suggest context, but it cannot prove carb ice by itself.",
    contexts: [
      "Weather and humidity",
      "Carb-heat use",
      "Low-power operation",
      "Sensor calibration",
      "Power changes unrelated to ice"
    ],
    checks: [
      "Compare carb temperature and OAT with RPM/MAP changes.",
      "Review weather and carb-heat use if known.",
      "Check whether the pattern appears in similar power settings.",
      "Treat repeated cues as context to discuss, not as a confirmed cause."
    ]
  },
  coolingScore: {
    label: "Cooling",
    title: "Cooling cue is repeating",
    action: "Compare climb speed, power, mixture, OAT, baffling, and cowl-seal history.",
    meaning: "Cooling cue means a cylinder is repeatedly running hotter than the other cylinders in similar flight conditions. In simple terms, it may not be cooling as well as the others.",
    why: "Repeated cooling cues often help owners decide whether to review technique, airflow, baffling, or cowl seals. It is not the same as a confirmed cylinder problem.",
    contexts: [
      "Baffling/cowl seals",
      "Climb speed",
      "High power",
      "Mixture setting",
      "OAT",
      "Probe placement"
    ],
    checks: [
      "Check whether the same cylinder repeats, especially in climb.",
      "Compare airspeed, power, fuel flow, OAT, and altitude.",
      "Look for recent baffling, cowl-seal, cowling, or probe changes.",
      "If persistent in comparable climbs, ask your mechanic to review cooling airflow and probe placement."
    ]
  },
  coolingRateScore: {
    label: "Cooling Rate",
    title: "Rapid cooling cue is repeating",
    action: "Compare descent or power-reduction technique and watch whether the same cylinder repeats.",
    meaning: "Rapid cooling cue means a cylinder is cooling down faster than expected during descents or power reductions. In simple terms, it is losing heat quicker than we would normally want to see.",
    why: "Rapid cooling trends are usually about operating technique and descent profile first. Same-cylinder or unusually abrupt repeats may deserve closer review.",
    contexts: [
      "Power pulled back quickly",
      "High-speed descent",
      "Mixture or cowl flap changes",
      "Short noisy window",
      "Probe behavior"
    ],
    checks: [
      "Compare descent rate, power changes, airspeed, and mixture context.",
      "Check whether one cylinder cools faster than peers or all cylinders cool together.",
      "Review pilot technique before assuming a mechanical issue.",
      "If one cylinder repeatedly behaves differently, compare probe quality and discuss with a mechanic."
    ]
  },
  chargingScore: {
    label: "Start Charge",
    title: "Start-charge recovery needs review",
    action: "Compare startup bus voltage and alternator/current behavior across recent starts.",
    meaning: "This trend watches bus-voltage recovery after engine start.",
    why: "Weak or slow voltage recovery can be an early electrical-system review cue, but it can also reflect battery condition, alternator load, or how the log captured startup.",
    contexts: [
      "Battery state",
      "Alternator coming online late",
      "High electrical load",
      "Short/incomplete startup data",
      "Sensor scaling"
    ],
    checks: [
      "Compare bus voltage before start, during start, and after alternator recovery.",
      "Check amps/current if available.",
      "Look for a change after battery, alternator, regulator, or avionics work.",
      "Discuss repeated weak recovery with a mechanic or avionics shop."
    ]
  },
  electricalScore: {
    label: "Electrical",
    title: "Electrical behavior needs review",
    action: "Compare bus voltage/current history and check whether the pattern started after equipment or alternator work.",
    meaning: "Electrical behavior means the airplane's electrical system may be showing uneven voltage or charging behavior across flights. In simple terms, the power system may not be staying as steady as we'd expect.",
    why: "Electrical trends can reveal charging or load changes before they become obvious in flight. Missing or noisy electrical channels can also create misleading cues.",
    contexts: [
      "Alternator/regulator behavior",
      "Battery condition",
      "Avionics load changes",
      "Sensor scaling or missing current data"
    ],
    checks: [
      "Compare bus voltage range across similar flights.",
      "Check current/amps if available.",
      "Look for when the trend began relative to electrical or avionics work.",
      "Ask a mechanic or avionics shop to review repeated voltage/current cues."
    ]
  },
};

export function createTrendSignalContext({
  trendAffectedRows,
  getCurrentTrendRows,
  getCurrentTrendAircraft,
  selectedTrendAircraft,
  getAircraftProfile,
  engineDescriptorsForData,
  engineNarrativeCylinderLabel,
  trendSensorSpecificMeaning,
  trendSensorSpecificWhy,
  trendSensorSpecificContexts,
  trendTurboSpecificMeaning,
  trendTurboSpecificWhy,
  trendTurboSpecificContexts,
  trendDivergenceSpecificMeaning,
  trendDivergenceSpecificWhy,
  trendDivergenceSpecificContexts,
  trendCombustionSpecificMeaning,
  trendCombustionSpecificWhy,
  trendCombustionSpecificContexts,
  trendCombustionSpecificChecks,
}) {
  function trendCylinderRowsFromContext(context) {
    if (Array.isArray(context)) return context;
    if (context?.recentFlaggedRows || context?.flaggedRows) return trendAffectedRows(context);
    if (context && typeof context === "object" && context.id) return [context];
    const current = getCurrentTrendRows();
    return Array.isArray(current) ? current : [];
  }

  function trendCylinderAircraftFromRows(rows) {
    return rows.find((row) => row?.aircraft)?.aircraft
      ?? getCurrentTrendAircraft()
      ?? selectedTrendAircraft();
  }

  function addTrendCylinderNumber(numbers, value) {
    const number = Number(value);
    if (Number.isFinite(number) && number > 0) numbers.add(number);
  }

  function trendCylinderNumbersForRows(rows) {
    const numbers = new Set();
    rows.forEach((row) => {
      [
        row?.hottestCylinder,
        row?.valveCylinder,
        row?.combustionCylinder,
        row?.descentEgtDropoutCylinder,
        row?.inductionLeakCylinder,
        row?.magCylinder,
        row?.coolingCylinder,
        row?.coolingRateCylinder,
      ].forEach((value) => addTrendCylinderNumber(numbers, value));
      (row?.divergenceCylinders ?? []).forEach((value) => addTrendCylinderNumber(numbers, value));
      (row?.chtByCylinder ?? []).forEach((item) => addTrendCylinderNumber(numbers, item?.cylinder));
      (row?.divergenceEvents ?? []).forEach((event) => {
        addTrendCylinderNumber(numbers, event?.cylinder);
        (event?.cylinders ?? []).forEach((value) => addTrendCylinderNumber(numbers, value));
      });
      (row?.descentEgtDropoutEvents ?? []).forEach((event) => addTrendCylinderNumber(numbers, event?.cylinder));
      (row?.coolingRateEvents ?? []).forEach((event) => addTrendCylinderNumber(numbers, event?.cylinder));
    });
    return numbers;
  }

  function trendEngineDescriptorsForCylinderContext(context, extraCylinder = null) {
    const rows = trendCylinderRowsFromContext(context);
    const aircraft = trendCylinderAircraftFromRows(rows);
    const profile = getAircraftProfile(aircraft);
    const numbers = trendCylinderNumbersForRows(rows);
    addTrendCylinderNumber(numbers, extraCylinder);
    const syntheticPoint = {};
    numbers.forEach((number) => {
      syntheticPoint[`egt${number}`] = 1;
      syntheticPoint[`cht${number}`] = 1;
    });
    return engineDescriptorsForData(Object.keys(syntheticPoint).length ? [syntheticPoint] : [], profile, {
      allowThermalTwinInference: true,
    });
  }

  function trendCylinderLabel(value, context = null) {
    if (!Number.isFinite(value)) return "";
    const descriptors = trendEngineDescriptorsForCylinderContext(context, value);
    return engineNarrativeCylinderLabel(value, descriptors);
  }

  function trendCylinderListLabel(values, context = null) {
    const cylinders = [...new Set((values ?? []).filter(Number.isFinite))].sort((a, b) => a - b);
    if (!cylinders.length) return "";
    if (cylinders.length > 3) return "multiple cylinders";
    const descriptors = trendEngineDescriptorsForCylinderContext(context, cylinders[0]);
    const hasMultipleEngines = (descriptors?.length ?? 0) > 1;
    if (hasMultipleEngines) {
      return cylinders.map((cylinder) => engineNarrativeCylinderLabel(cylinder, descriptors)).join(", ");
    }
    return cylinders.length === 1 ? trendCylinderLabel(cylinders[0], context) : `Cylinders ${cylinders.join(", ")}`;
  }

  // Per-signal render wiring: only the parts that need this factory's closures and
  // injected dependencies. The owner-facing copy is pulled from the TREND_METRIC_COPY
  // registry above by key and merged in below, preserving this catalog order.
  const trendSignalWiring = [
    {
      key: "sensorScore",
      affected: (row) => row.sensorLabel,
      specificMeaning: trendSensorSpecificMeaning,
      specificWhy: trendSensorSpecificWhy,
      specificContexts: trendSensorSpecificContexts,
    },
    {
      key: "turboScore",
      specificMeaning: trendTurboSpecificMeaning,
      specificWhy: trendTurboSpecificWhy,
      specificContexts: trendTurboSpecificContexts,
    },
    { key: "gamiSweepScore" },
    {
      key: "oilSystemScore",
      affected: (row) => row.oilSystemChannelLabel,
    },
    {
      key: "chtScore",
      affected: (row) => trendCylinderLabel(row.hottestCylinder, row),
    },
    { key: "egtScore" },
    {
      key: "valveScore",
      affected: (row) => trendCylinderLabel(row.valveCylinder, row),
    },
    {
      key: "combustionScore",
      affected: (row) => trendCylinderLabel(row.combustionCylinder, row),
      specificMeaning: trendCombustionSpecificMeaning,
      specificWhy: trendCombustionSpecificWhy,
      specificContexts: trendCombustionSpecificContexts,
      specificChecks: trendCombustionSpecificChecks,
    },
    {
      key: "inductionLeakScore",
      affected: (row) => trendCylinderLabel(row.inductionLeakCylinder, row),
      flagged: (row) => Number.isFinite(row?.inductionLeakStartTime)
        || (Number.isFinite(row?.inductionLeakCylinderEgtChange ?? row?.inductionLeakEgtChange) && (row.inductionLeakCylinderEgtChange ?? row.inductionLeakEgtChange) > 0)
        || (Number.isFinite(row?.inductionLeakFuelFlowChange) && Math.abs(row.inductionLeakFuelFlowChange) > 0.05),
    },
    {
      key: "magScore",
      affected: (row) => trendCylinderLabel(row.magCylinder, row),
    },
    { key: "carbIceScore" },
    {
      key: "coolingScore",
      affected: (row) => trendCylinderLabel(row.coolingCylinder, row),
    },
    {
      key: "coolingRateScore",
      affected: (row) => trendCylinderLabel(row.coolingRateCylinder, row),
    },
    { key: "chargingScore" },
    { key: "electricalScore" },
  ];

  // Merge the per-metric copy with the render wiring. Spread order is copy-then-wiring
  // so a signal can never silently override its registry prose; `key` is set explicitly.
  const trendSignalDefinitions = trendSignalWiring.map(({ key, ...wiring }) => {
    const copy = TREND_METRIC_COPY[key];
    if (!copy) throw new Error(`Missing trend metric copy for signal "${key}"`);
    return { key, ...copy, ...wiring };
  });

  return {
    trendCylinderRowsFromContext,
    trendCylinderAircraftFromRows,
    addTrendCylinderNumber,
    trendCylinderNumbersForRows,
    trendEngineDescriptorsForCylinderContext,
    trendCylinderLabel,
    trendCylinderListLabel,
    trendSignalDefinitions,
  };
}
